import { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense } from 'react';
import { useFocusEffect } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import { View, ActivityIndicator, Alert, ScrollView, TextInput, Pressable, FlatList, StyleSheet, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Screen } from '@/components/layout/Screen';
import { Card } from '@/components/ui/Card';
import { SubHeading, Body, Caption, ThemedText } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { useUserStore } from '@/state/userStore';
import { usePremiumGateStore } from '@/state/premiumGateStore';
import { hasPremiumAccess } from '@/services/entitlements';
import { logger } from '@/services/logger';
import { subscribeToUserDocuments, deleteDocument, listDocuments, type Document } from '@/services/documentVault';
import { DocumentCard } from '@/components/documents/DocumentCard';
import { EmptyDocumentsState } from '@/components/documents/EmptyDocumentsState';
import { DocumentUploadModal } from '@/components/documents/DocumentUploadModal';
import { DocumentEditModal } from '@/components/documents/DocumentEditModal';
import { DocumentSecuritySettings } from '@/components/documents/DocumentSecuritySettings';
import { Ionicons } from '@expo/vector-icons';
import { PrivacyDashboard } from '@/components/privacy/PrivacyDashboard';
import { useToast } from '@/components/ui/ToastProvider';
import { Button } from '@/components/ui/Button';
import { LoadingState } from '@/components/ui/LoadingState';
import { DocumentCardSkeleton } from '@/components/ui/SkeletonLoader';
import {
  getOfflineDocuments,
  getOfflineDocumentsSize,
  formatFileSize,
  mapOfflineDocumentsToVaultDocuments,
  clearAllOfflineDocuments,
  pruneOfflineDocumentsWithMissingFiles,
  cacheVaultManualDocumentsForOffline,
  getCachedOfflineManualDocuments,
  downloadAllPendingFileDocumentsForOffline,
} from '@/services/offlineDocuments';
import { networkMonitor } from '@/services/networkMonitor';
import { useAutoDownloadDocuments } from '@/hooks/useAutoDownloadDocuments';
import { getAutoDownloadDocumentsEnabled } from '@/services/autoDownloadPreference';
import { subscribeToUserTrips, type Trip } from '@/services/trips';
import { AppState } from 'react-native';
import {
  getDocumentsWithExpirationStatus,
  getExpiredDocuments,
  getDocumentsExpiringWithin,
  sortDocumentsByExpiration,
  type ExpirationStatus,
} from '@/utils/documentExpiration';
import { useDocumentExpirationNotifications } from '@/hooks/useDocumentExpirationNotifications';
import { OfflineAccessCardShell } from '@/components/offline/OfflineAccessCardShell';
import { documentHasMetadataContent } from '@/components/documents/documentMetadataRows';
import { refreshOfflineReceiptCopiesForTrips } from '@/services/offlineTrips';
import { traceModuleEvaluation } from '@/utils/moduleTrace';
import { TripErrorBoundary } from '@/utils/errorBoundary';

traceModuleEvaluation('app/(tabs)/documents');

const DocumentViewer = lazy(() =>
  import('@/components/documents/DocumentViewer').then((module) => ({ default: module.DocumentViewer }))
);

type DocumentsScreenProps = {
  hideHeader?: boolean;
};

function DocumentsScreenInner({ hideHeader = false }: DocumentsScreenProps = {}) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const isFocused = useIsFocused();
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const isPremium = useUserStore((state) =>
    hasPremiumAccess({
      isPremium: state.profile?.isPremium,
      planTier: state.profile?.planTier,
      entitlement: state.profile?.entitlement,
    }),
  );
  const openPremiumGate = usePremiumGateStore((s) => s.open);
  const { showError } = useToast();
  
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadModalEntryMode, setUploadModalEntryMode] = useState<'upload' | 'manual'>('upload');
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [showViewer, setShowViewer] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPrivacyDashboard, setShowPrivacyDashboard] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [removingOfflineCopies, setRemovingOfflineCopies] = useState(false);
  const [offlineStorageSize, setOfflineStorageSize] = useState(0);
  const [offlineDocumentCount, setOfflineDocumentCount] = useState(0);
  const [offlineDocumentIds, setOfflineDocumentIds] = useState<Set<string>>(() => new Set());
  const [trips, setTrips] = useState<Trip[]>([]);
  const [autoDownloadEnabled, setAutoDownloadEnabled] = useState(true);
  const documentsRef = useRef<Document[]>([]);
  documentsRef.current = documents;

  // Subscribe to documents updates
  useEffect(() => {
    if (!firebaseUser?.uid) {
      setDocuments([]);
      setLoading(false);
      return;
    }

    if (!isFocused) {
      setLoading(false);
      return;
    }

    if (documentsRef.current.length === 0) {
      setLoading(true);
    }
    let hasReceivedData = false;
    let cancelled = false;

    void Promise.all([getOfflineDocuments(), getCachedOfflineManualDocuments(firebaseUser.uid)])
      .then(([offline, cachedVault]) => {
        if (cancelled) return;
        const fileDocs = mapOfflineDocumentsToVaultDocuments(offline, firebaseUser.uid);
        const byId = new Map<string, Document>();
        for (const d of cachedVault) {
          byId.set(d.id, d);
        }
        for (const d of fileDocs) {
          const prev = byId.get(d.id);
          if (prev) {
            byId.set(d.id, {
              ...prev,
              ...d,
              metadata:
                d.metadata && documentHasMetadataContent(d.metadata) ? d.metadata : prev.metadata,
              contentType: d.contentType ?? prev.contentType,
              size: d.size ?? prev.size,
              name: d.name || prev.name,
              expiresOn: d.expiresOn ?? prev.expiresOn,
            });
          } else {
            byId.set(d.id, d);
          }
        }
        const seed = Array.from(byId.values());
        if (seed.length === 0) return;
        setDocuments((prev) => (prev.length > 0 ? prev : seed));
        hasReceivedData = true;
        setLoading(false);
      })
      .catch((error) => {
        logger.warn(
          '[DocumentsScreen] Failed to seed from offline cache',
          undefined,
          error instanceof Error ? error : new Error(String(error)),
        );
      });

    const unsubscribe = subscribeToUserDocuments(firebaseUser.uid, (userDocuments) => {
      console.log('[DocumentsScreen] Received documents:', userDocuments.length);
      hasReceivedData = true;
      setDocuments((prev) => {
        if (
          networkMonitor.shouldUseOfflineDataFallback() &&
          prev.length > 0 &&
          userDocuments.length === 0
        ) {
          return prev;
        }
        void cacheVaultManualDocumentsForOffline(firebaseUser.uid, userDocuments);
        return userDocuments;
      });
      setLoading(false);
    });

    // Set a timeout to ensure loading state doesn't get stuck
    const timeoutId = setTimeout(() => {
      if (!hasReceivedData) {
        console.warn('[DocumentsScreen] Subscription timeout - clearing loading state');
        setLoading(false);
      }
    }, 10000); // 10 second timeout

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [firebaseUser?.uid, isFocused]);

  // Monitor document expirations and send notifications
  useDocumentExpirationNotifications(firebaseUser?.uid || null);

  // Subscribe to trips for auto-download
  useEffect(() => {
    if (!firebaseUser?.uid) {
      setTrips([]);
      return;
    }

    if (!isFocused) {
      return;
    }

    const unsubscribe = subscribeToUserTrips(firebaseUser.uid, (userTrips) => {
      setTrips((prev) => {
        if (
          networkMonitor.shouldUseOfflineDataFallback() &&
          prev.length > 0 &&
          userTrips.length === 0
        ) {
          return prev;
        }
        return userTrips;
      });
    });

    return () => unsubscribe();
  }, [firebaseUser?.uid, isFocused]);

  const refreshOfflineInventory = useCallback(async () => {
    try {
      await pruneOfflineDocumentsWithMissingFiles();
      const [size, offlineDocs] = await Promise.all([
        getOfflineDocumentsSize(),
        getOfflineDocuments(),
      ]);
      setOfflineStorageSize(size);
      setOfflineDocumentCount(offlineDocs.length);
      setOfflineDocumentIds(new Set(offlineDocs.map((d) => d.documentId)));
    } catch (error) {
      console.error('[DocumentsScreen] Error refreshing offline inventory:', error);
    }
  }, []);

  useEffect(() => {
    void getAutoDownloadDocumentsEnabled()
      .then(setAutoDownloadEnabled)
      .catch((error) => console.error('[DocumentsScreen] Error loading auto-download setting:', error));
  }, []);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      void getAutoDownloadDocumentsEnabled()
        .then(setAutoDownloadEnabled)
        .catch((error) => {
          logger.warn(
            '[DocumentsScreen] Failed to read auto-download setting on focus',
            undefined,
            error instanceof Error ? error : new Error(String(error)),
          );
        });
      if (firebaseUser?.uid) {
        void listDocuments()
          .then((latest) => {
            if (cancelled) return;
            setDocuments((prev) => {
              if (
                networkMonitor.shouldUseOfflineDataFallback() &&
                prev.length > 0 &&
                latest.length === 0
              ) {
                return prev;
              }
              return latest;
            });
          })
          .catch(() => {
            // Leave the realtime listener as the source of truth if this fallback refresh fails.
          })
          .finally(() => {
            if (!cancelled) {
              setLoading(false);
            }
          });
      }
      void refreshOfflineInventory();
      return () => {
        cancelled = true;
      };
    }, [firebaseUser?.uid, refreshOfflineInventory]),
  );

  // Auto-download documents for active trips
  const getDocumentsForTrip = useCallback(async (tripId: string): Promise<Document[]> => {
    return documents.filter((doc) => Array.isArray(doc.tripIds) && doc.tripIds.includes(tripId));
  }, [documents]);

  useAutoDownloadDocuments({
    trips,
    getDocumentsForTrip,
    // Don't start background work until we have user docs.
    enabled: isPremium && autoDownloadEnabled && loading === false && documents.length > 0,
  });

  // If Firestore listeners are slow to deliver (common after cold start), do a lightweight refresh on app resume.
  useEffect(() => {
    if (!firebaseUser?.uid || !isFocused) return;
    const sub = AppState.addEventListener('change', (state) => {
      if (state !== 'active') return;
      if (loading) {
        void (async () => {
          try {
            const latest = await listDocuments();
            setDocuments(latest);
          } catch {
            // ignore
          } finally {
            setLoading(false);
          }
        })();
      }
    });
    return () => {
      sub.remove();
    };
  }, [firebaseUser?.uid, isFocused, loading]);

  useEffect(() => {
    void refreshOfflineInventory();
    const interval = setInterval(() => {
      void refreshOfflineInventory();
    }, 10000);
    return () => clearInterval(interval);
  }, [refreshOfflineInventory]);

  const downloadableDocumentsCount = useMemo(
    () => documents.filter((doc) => doc.entryType !== 'manual').length,
    [documents],
  );
  const manualDocumentsCount = useMemo(
    () => documents.length - downloadableDocumentsCount,
    [documents.length, downloadableDocumentsCount],
  );
  const pendingOfflineDownloads = useMemo(
    () =>
      documents.filter((doc) => doc.entryType !== 'manual' && !offlineDocumentIds.has(doc.id)).length,
    [documents, offlineDocumentIds],
  );

  const handleRemoveOfflineCopies = async () => {
    const confirmed = await new Promise<boolean>((resolve) => {
      Alert.alert(
        'Remove Offline Copies',
        'This will delete all locally saved documents from this device. You can re-download them later.',
        [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          { text: 'Remove', style: 'destructive', onPress: () => resolve(true) },
        ],
      );
    });

    if (!confirmed) return;

    setRemovingOfflineCopies(true);
    try {
      await clearAllOfflineDocuments();
      await refreshOfflineInventory();
    } catch (error) {
      console.error('[DocumentsScreen] Error removing offline copies:', error);
      showError('Failed to remove offline copies. Please try again.');
    } finally {
      setRemovingOfflineCopies(false);
    }
  };

  const handleDownloadAll = async () => {
    if (!isPremium) {
      openPremiumGate({
        feature: 'offline_documents',
        title: 'Offline documents',
        message: 'Offline access is a Premium feature. Upgrade to download documents for offline use.',
        source: 'feature_gate',
      });
      return;
    }
    if (!networkMonitor.isOnline()) {
      Alert.alert('Offline', 'You need to be online to download documents.');
      return;
    }

    const documentsToDownload = documents.filter(
      (doc) => doc.entryType !== 'manual' && !offlineDocumentIds.has(doc.id),
    );

    if (documentsToDownload.length === 0) {
      Alert.alert(
        'Up to date',
        'Every file-backed document is already saved on this device for offline viewing.',
      );
      return;
    }

    const confirmed = await new Promise<boolean>((resolve) => {
      Alert.alert(
        'Download All Documents',
        `Download ${documentsToDownload.length} document${documentsToDownload.length !== 1 ? 's' : ''} for offline access? This may use significant storage space.`,
        [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          { text: 'Download', onPress: () => resolve(true) },
        ]
      );
    });

    if (!confirmed) return;

    setDownloadingAll(true);
    try {
      const touchedTripIds = new Set<string>();
      const { success, failed, downloadedIds } = await downloadAllPendingFileDocumentsForOffline(
        documentsToDownload,
      );
      const downloadedSet = new Set(downloadedIds);
      for (const document of documentsToDownload) {
        if (!downloadedSet.has(document.id)) continue;
        for (const tid of document.tripIds ?? []) {
          if (typeof tid === 'string' && tid.length > 0) {
            touchedTripIds.add(tid);
          }
        }
      }

      await refreshOfflineInventory();

      if (touchedTripIds.size > 0) {
        await refreshOfflineReceiptCopiesForTrips(touchedTripIds, (tripId) =>
          trips.find((t) => t.id === tripId),
        );
      }

      Alert.alert(
        'Download Complete',
        `Successfully downloaded ${success} document${success !== 1 ? 's' : ''}.${failed > 0 ? ` ${failed} failed.` : ''}`
      );
    } catch (error) {
      console.error('[DocumentsScreen] Error downloading documents:', error);
      Alert.alert('Error', 'Failed to download some documents. Please try again.');
    } finally {
      setDownloadingAll(false);
    }
  };

  const headerAction = isPremium ? (
    <View style={{ flexDirection: 'row', gap: theme.spacing(1), alignItems: 'center' }}>
      {offlineStorageSize > 0 && (
        <View style={{ 
          flexDirection: 'row', 
          alignItems: 'center', 
          gap: theme.spacing(0.5),
          paddingHorizontal: theme.spacing(1),
          paddingVertical: theme.spacing(0.5),
          backgroundColor: theme.colors.surface,
          borderRadius: theme.radii.md,
        }}>
          <Ionicons name="cloud-done" size={14} color={theme.colors.primary} />
          <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
            {formatFileSize(offlineStorageSize)}
          </Caption>
        </View>
      )}
      <Pressable
        onPress={handleDownloadAll}
        disabled={downloadingAll || pendingOfflineDownloads === 0}
        style={({ pressed }) => [
          {
            padding: theme.spacing(1),
            borderRadius: theme.radii.md,
            backgroundColor: pressed ? theme.colors.primary + '20' : 'transparent',
            opacity: (downloadingAll || pendingOfflineDownloads === 0) ? 0.5 : 1,
          },
        ]}
      >
        {downloadingAll ? (
          <ActivityIndicator size="small" color={theme.colors.primary} />
        ) : (
          <Ionicons name="download-outline" size={24} color={theme.colors.primary} />
        )}
      </Pressable>
    </View>
  ) : undefined;


  const handleDocumentPress = (document: Document) => {
    setSelectedDocument(document);
    setShowViewer(true);
  };

  const handleEditDocument = (document: Document) => {
    // iOS can be finicky about stacking multiple modals.
    // Close the viewer first, then present the edit modal on the next tick.
    setShowViewer(false);
    setSelectedDocument(null);
    setTimeout(() => {
      setEditingDocument(document);
      setShowEditModal(true);
    }, 50);
  };

  const openUploadModal = useCallback(
    (mode: 'upload' | 'manual' = 'upload') => {
      if (mode === 'upload' && !isPremium) {
        openPremiumGate({
          feature: 'document_upload',
          title: 'Upload documents',
          message: 'Document uploads are a Premium feature. You can still add documents manually on the free plan.',
          source: 'feature_gate',
        });
        return;
      }

      setUploadModalEntryMode(mode);
      setShowUploadModal(true);
    },
    [isPremium, openPremiumGate],
  );

  const handleDeleteDocument = (document: Document) => {
    console.log('[DocumentsScreen] Delete requested for:', document.id, document.name);
    const doDelete = async () => {
      console.log('[DocumentsScreen] Delete confirmed, calling deleteDocument...');
      try {
        await deleteDocument(document.id);
        console.log('[DocumentsScreen] Delete successful');
        // Optimistic remove to reflect immediately while subscription updates
        setDocuments((prev) => prev.filter((d) => d.id !== document.id));
        if (selectedDocument?.id === document.id) {
          setShowViewer(false);
          setSelectedDocument(null);
        }
      } catch (error: any) {
        console.error('[DocumentsScreen] Error deleting document:', error);
        const errorMessage = error?.message || 'Failed to delete document. Please try again.';
        showError(errorMessage);
      }
    };

    // Web: Alert.alert can be flaky; use window.confirm
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.confirm) {
      const confirmed = window.confirm(
        `Delete Document\n\nAre you sure you want to delete "${document.name}"? This action cannot be undone.`
      );
      if (confirmed) void doDelete();
      return;
    }

    Alert.alert('Delete Document', `Are you sure you want to delete "${document.name}"? This action cannot be undone.`, [
      { text: 'Cancel', style: 'cancel', onPress: () => console.log('[DocumentsScreen] Delete cancelled') },
      { text: 'Delete', style: 'destructive', onPress: () => void doDelete() },
    ]);
  };

  const handleUploadSuccess = async () => {
    setShowUploadModal(false);
    // Documents should update via subscription, but on some iOS builds Firestore listeners can
    // fail to deliver updates until the app restarts (observed after certain internal assertions).
    // As a fallback, force-refresh once after a successful upload.
    if (firebaseUser?.uid) {
      try {
        const latest = await listDocuments();
        setDocuments(latest);
      } catch (e: any) {
        // Non-fatal; subscription should eventually catch up.
        console.warn('[DocumentsScreen] listDocuments fallback failed', e?.message || e);
      }
    }
  };

  // Calculate expiration status for all documents
  const documentsWithStatus = useMemo(
    () => getDocumentsWithExpirationStatus(documents),
    [documents]
  );

  // Get expired documents
  const expired = useMemo(() => getExpiredDocuments(documents), [documents]);

  // Get documents expiring within 90 days
  const expiringVerySoon = useMemo(
    () => getDocumentsExpiringWithin(documents, 30),
    [documents]
  );

  const expiringSoon = useMemo(() => {
    const within90 = getDocumentsExpiringWithin(documents, 90);
    const verySoonIds = new Set(expiringVerySoon.map((d) => d.id));
    const expiredIds = new Set(expired.map((d) => d.id));
    return within90.filter((doc) => !verySoonIds.has(doc.id) && !expiredIds.has(doc.id));
  }, [documents, expiringVerySoon, expired]);

  // Filter and sort documents
  const filteredAndSortedDocuments = useMemo(() => {
    let filtered = documents;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(doc => 
        doc.name.toLowerCase().includes(query) ||
        doc.type?.toLowerCase().includes(query) ||
        doc.category?.toLowerCase().includes(query)
      );
    }
    
    // Sort by expiration (soonest first)
    return sortDocumentsByExpiration(filtered, true);
  }, [documents, searchQuery]);

  if (loading) {
    return (
      <Screen title={hideHeader ? undefined : "Secure documents"} subtitle={hideHeader ? undefined : "Encrypted storage for passports, visas, and health records with biometric access."}>
        <Card>
          <View style={{ gap: theme.spacing(2) }}>
            {Array.from({ length: 3 }).map((_, index) => (
              <DocumentCardSkeleton key={index} />
            ))}
          </View>
        </Card>
      </Screen>
    );
  }

  return (
    <Screen
      title={hideHeader ? undefined : "Secure documents"}
      subtitle={hideHeader ? undefined : "Encrypted storage for passports, visas, and health records with biometric access."}
      headerAction={hideHeader ? undefined : headerAction}
      contentStyle={{ backgroundColor: 'transparent' }}
    >
      {isPremium && (offlineDocumentCount > 0 || downloadableDocumentsCount > 0) ? (
        <OfflineAccessCardShell
          collapsible
          defaultExpanded={false}
          icon="cloud-done-outline"
          active={offlineDocumentCount > 0}
          title="Offline access"
          subtitle={
            offlineDocumentCount > 0
              ? `${offlineDocumentCount} document${offlineDocumentCount !== 1 ? 's are' : ' is'} saved on this device for offline viewing.`
              : 'Save your travel documents on this device so they are available when you lose signal.'
          }
          footer={
            <View style={{ gap: theme.spacing(1) }}>
              {offlineDocumentCount > 0 ? (
                <Caption color={theme.colors.textSecondary}>
                  {formatFileSize(offlineStorageSize)} stored locally
                </Caption>
              ) : null}
              {pendingOfflineDownloads > 0 ? (
                <Caption color={theme.colors.textSecondary}>
                  {pendingOfflineDownloads} more file-backed document
                  {pendingOfflineDownloads !== 1 ? 's are' : ' is'} available to download for offline access.
                </Caption>
              ) : null}
              {manualDocumentsCount > 0 ? (
                <Caption color={theme.colors.textSecondary}>
                  {manualDocumentsCount} manual {manualDocumentsCount !== 1 ? 'entries' : 'entry'} — details stay on this
                  device after you have opened Documents while online; file attachments still need a separate download.
                </Caption>
              ) : null}
              <Caption color={theme.colors.textSecondary}>
                Auto-download for new documents can be changed in Profile → Offline mode.
              </Caption>
              <View style={{ flexDirection: 'row', gap: theme.spacing(1), flexWrap: 'wrap' }}>
                <Button
                  label={
                    downloadingAll
                      ? 'Downloading...'
                      : pendingOfflineDownloads > 0
                        ? offlineDocumentCount > 0
                          ? 'Download remaining'
                          : 'Download all'
                        : 'Offline copies up to date'
                  }
                  variant="secondary"
                  onPress={handleDownloadAll}
                  disabled={downloadingAll || pendingOfflineDownloads === 0}
                  icon={
                    downloadingAll
                      ? <ActivityIndicator size="small" color={theme.colors.primary} />
                      : <Ionicons name="download-outline" size={16} color={theme.colors.primary} />
                  }
                />
                {offlineDocumentCount > 0 ? (
                  <Button
                    label={removingOfflineCopies ? 'Removing…' : 'Remove offline copies'}
                    variant="secondary"
                    onPress={handleRemoveOfflineCopies}
                    disabled={removingOfflineCopies || downloadingAll}
                    icon={
                      removingOfflineCopies ? (
                        <ActivityIndicator size="small" color={theme.colors.error || '#FF3B30'} />
                      ) : (
                        <Ionicons name="trash-outline" size={16} color={theme.colors.error || '#FF3B30'} />
                      )
                    }
                  />
                ) : null}
              </View>
            </View>
          }
        />
      ) : null}
      {documents.length === 0 ? (
        <EmptyDocumentsState onUpload={() => openUploadModal('upload')} onManualEntry={() => openUploadModal('manual')} />
      ) : (
        <View style={{ gap: theme.spacing(3), backgroundColor: theme.colors.background, flex: 1 }}>
          {/* Search Bar */}
          <Card variant="outlined">
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
              <Ionicons name="search-outline" size={20} color={theme.colors.textSecondary} />
              <TextInput
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholder="Search documents..."
                placeholderTextColor={theme.colors.textSecondary}
                style={{
                  flex: 1,
                  fontSize: 16,
                  color: theme.colors.text,
                  paddingVertical: theme.spacing(1),
                  minHeight: 44, // Accessibility: minimum touch target
                }}
                accessibilityLabel="Search documents"
                accessibilityHint="Type to filter documents by name, type, or category"
              />
              {searchQuery.length > 0 && (
                <Pressable
                  onPress={() => setSearchQuery('')}
                  style={{ padding: theme.spacing(0.5) }}
                  accessibilityRole="button"
                  accessibilityLabel="Clear search"
                >
                  <Ionicons name="close-circle" size={20} color={theme.colors.textSecondary} />
                </Pressable>
              )}
            </View>
          </Card>

          {/* Permanent actions (always visible) */}
          <Card variant="outlined">
            <View style={{ gap: theme.spacing(1.5) }}>
              <SubHeading>Add a document</SubHeading>
              <Caption color={theme.colors.textSecondary}>
                Upload a file/photo or create a manual entry.
              </Caption>
              <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
                <Pressable
                  onPress={() => openUploadModal('upload')}
                  style={({ pressed }) => ({
                    flex: 1,
                    paddingVertical: theme.spacing(1.25),
                    paddingHorizontal: theme.spacing(1.25),
                    borderRadius: theme.radii.lg,
                    backgroundColor: pressed ? theme.colors.primary + '18' : theme.colors.primary + '10',
                    borderWidth: 1,
                    borderColor: theme.colors.primary + '35',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: theme.spacing(0.75),
                  })}
                  accessibilityRole="button"
                  accessibilityLabel="Upload document"
                >
                  <Ionicons name="cloud-upload-outline" size={18} color={theme.colors.primary} />
                  <ThemedText variant="bodyStrong" style={{ color: theme.colors.primary }}>
                    Upload
                  </ThemedText>
                </Pressable>
                <Pressable
                  onPress={() => openUploadModal('manual')}
                  style={({ pressed }) => ({
                    flex: 1,
                    paddingVertical: theme.spacing(1.25),
                    paddingHorizontal: theme.spacing(1.25),
                    borderRadius: theme.radii.lg,
                    backgroundColor: pressed ? theme.colors.surface : theme.colors.surface,
                    borderWidth: 1,
                    borderColor: theme.colors.borderMuted,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: theme.spacing(0.75),
                  })}
                  accessibilityRole="button"
                  accessibilityLabel="Add manual document entry"
                >
                  <Ionicons name="create-outline" size={18} color={theme.colors.text} />
                  <ThemedText variant="bodyStrong">Manual</ThemedText>
                </Pressable>
              </View>
            </View>
          </Card>
          {/* Upcoming Expirations Section */}
          {(expired.length > 0 || expiringVerySoon.length > 0 || expiringSoon.length > 0) && (
            <View
              style={{
                borderRadius: theme.radii.xl,
                padding: theme.spacing(2),
                backgroundColor: theme.colors.surface,
                borderWidth: StyleSheet.hairlineWidth,
                borderColor: theme.colors.borderMuted,
                gap: theme.spacing(1.5),
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), justifyContent: 'space-between' }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                  <Ionicons
                    name="warning"
                    size={22}
                    color="#EF4444"
                  />
                  <View>
                    <SubHeading style={{ fontSize: 18 }}>Expiration Alerts</SubHeading>
                    <Caption color={theme.colors.textSecondary}>
                      {expired.length > 0
                        ? 'Immediate action required'
                        : expiringVerySoon.length > 0
                        ? 'Renewal needed soon'
                        : 'Upcoming renewals'}
                    </Caption>
                  </View>
                </View>
                <View
                  style={{
                    backgroundColor: theme.colors.background,
                    paddingHorizontal: theme.spacing(1.25),
                    paddingVertical: theme.spacing(0.5),
                    borderRadius: theme.radii.lg,
                  }}
                >
                  <Caption color={theme.colors.textSecondary} style={{ fontWeight: '700' }}>
                    {expired.length + expiringVerySoon.length + expiringSoon.length}
                  </Caption>
                </View>
              </View>

              {/* Plain text list (no inner dark container / colored line) */}
              <View style={{ gap: theme.spacing(1) }}>
                {expired.length > 0 && (
                  <View style={{ gap: theme.spacing(0.25) }}>
                    <ThemedText variant="bodyStrong">
                      {`${expired.length} document${expired.length > 1 ? 's' : ''} expired`}
                    </ThemedText>
                    <Caption color={theme.colors.textSecondary}>
                      Update or remove expired documents to keep your vault current.
                    </Caption>
                    {expired.slice(0, 3).map((doc) => {
                      const status = documentsWithStatus.find((d) => d.id === doc.id)?.expirationStatus;
                      const suffix = status?.daysUntil ? ` (${status.daysUntil} days ago)` : '';
                      return (
                        <Caption key={doc.id} color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                          • {doc.name}
                          {suffix}
                        </Caption>
                      );
                    })}
                    {expired.length > 3 && (
                      <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                        • +{expired.length - 3} more
                      </Caption>
                    )}
                  </View>
                )}
                {expiringVerySoon.length > 0 && (
                  <View style={{ gap: theme.spacing(0.25) }}>
                    <ThemedText variant="bodyStrong">
                      {`${expiringVerySoon.length} document${expiringVerySoon.length > 1 ? 's' : ''} expiring in 30 days`}
                    </ThemedText>
                    <Caption color={theme.colors.textSecondary}>
                      Renew these documents urgently to avoid travel disruptions.
                    </Caption>
                    {expiringVerySoon.slice(0, 3).map((doc) => {
                      const status = documentsWithStatus.find((d) => d.id === doc.id)?.expirationStatus;
                      const suffix = status?.daysUntil ? ` (${status.daysUntil} days left)` : '';
                      return (
                        <Caption key={doc.id} color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                          • {doc.name}
                          {suffix}
                        </Caption>
                      );
                    })}
                    {expiringVerySoon.length > 3 && (
                      <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                        • +{expiringVerySoon.length - 3} more
                      </Caption>
                    )}
                  </View>
                )}
                {expiringSoon.length > 0 && (
                  <View style={{ gap: theme.spacing(0.25) }}>
                    <ThemedText variant="bodyStrong">
                      {`${expiringSoon.length} document${expiringSoon.length > 1 ? 's' : ''} expiring in 90 days`}
                    </ThemedText>
                    <Caption color={theme.colors.textSecondary}>
                      Plan ahead and renew these documents before they expire.
                    </Caption>
                    {expiringSoon.slice(0, 3).map((doc) => {
                      const status = documentsWithStatus.find((d) => d.id === doc.id)?.expirationStatus;
                      const suffix = status?.daysUntil ? ` (${status.daysUntil} days left)` : '';
                      return (
                        <Caption key={doc.id} color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                          • {doc.name}
                          {suffix}
                        </Caption>
                      );
                    })}
                    {expiringSoon.length > 3 && (
                      <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                        • +{expiringSoon.length - 3} more
                      </Caption>
                    )}
                  </View>
                )}
              </View>
            </View>
          )}

          {/* Documents List - Wallet Style Stacked Deck */}
          <View style={{ marginTop: theme.spacing(2), backgroundColor: 'transparent', paddingTop: theme.spacing(2), paddingBottom: theme.spacing(2) }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing(2), paddingHorizontal: theme.spacing(2) }}>
              <View>
                <SubHeading style={{ fontSize: 18, lineHeight: 24, marginBottom: theme.spacing(0.5), color: '#FFFFFF' }}>
                  Your Documents
                </SubHeading>
                <Caption style={{ fontSize: 13, color: theme.colors.textSecondary }}>
                  {documents.length} {documents.length === 1 ? 'document' : 'documents'} stored securely
                </Caption>
              </View>
              <View
                style={{
                  paddingHorizontal: theme.spacing(1.5),
                  paddingVertical: theme.spacing(0.75),
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  borderRadius: theme.radii.md,
                }}
              >
                <ThemedText variant="bodyStrong" style={{ fontSize: 14, color: '#FFFFFF' }}>
                  {documents.length}
                </ThemedText>
              </View>
            </View>
            {filteredAndSortedDocuments.length > 0 ? (
              <View style={{ paddingBottom: theme.spacing(2), gap: theme.spacing(2) }}>
                {filteredAndSortedDocuments.map((doc, index) => (
                  <DocumentCard
                    key={doc.id}
                    document={doc}
                    onPress={() => handleDocumentPress(doc)}
                    onDelete={() => handleDeleteDocument(doc)}
                    index={index}
                    totalCards={filteredAndSortedDocuments.length}
                  />
                ))}
              </View>
            ) : (
              <View style={{ padding: theme.spacing(3), alignItems: 'center' }}>
                <Ionicons name="search-outline" size={48} color={theme.colors.textSecondary} />
                <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(1), textAlign: 'center' }}>
                  No documents match "{searchQuery}"
                </Body>
              </View>
            )}
          </View>

          {/* Security Settings */}
          <DocumentSecuritySettings />

          {/* Privacy Information */}
          <Card variant="outlined">
            <Pressable
              onPress={() => setShowPrivacyDashboard(true)}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: theme.spacing(1.5),
                opacity: pressed ? 0.7 : 1,
              })}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1.5), flex: 1 }}>
                <Ionicons name="shield-checkmark" size={24} color={theme.colors.primary} />
                <View style={{ flex: 1 }}>
                  <SubHeading style={{ fontSize: 16 }}>Privacy & Security</SubHeading>
                  <Caption color={theme.colors.textSecondary} style={{ marginTop: 2 }}>
                    We can't see your data — only you can
                  </Caption>
                </View>
              </View>
              <Ionicons name="chevron-forward-outline" size={20} color={theme.colors.textSecondary} />
            </Pressable>
          </Card>
        </View>
      )}

      {/* Upload Modal */}
      <TripErrorBoundary name="DocumentUploadModal">
        <DocumentUploadModal
          visible={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onSuccess={handleUploadSuccess}
          initialEntryMode={isPremium ? uploadModalEntryMode : 'manual'}
          allowUpload={isPremium}
        />
      </TripErrorBoundary>

      {/* Document Viewer Modal */}
      <TripErrorBoundary name="DocumentViewer">
        <Suspense fallback={null}>
          <DocumentViewer
            visible={showViewer}
            onClose={() => {
              setShowViewer(false);
              setSelectedDocument(null);
            }}
            document={selectedDocument}
            onDelete={selectedDocument ? () => handleDeleteDocument(selectedDocument) : undefined}
            onEdit={(doc) => handleEditDocument(doc)}
          />
        </Suspense>
      </TripErrorBoundary>

      <DocumentEditModal
        visible={showEditModal}
        document={editingDocument}
        onClose={() => {
          setShowEditModal(false);
          setEditingDocument(null);
        }}
      />

      {/* Privacy Dashboard Modal */}
      <PrivacyDashboard
        visible={showPrivacyDashboard}
        onClose={() => setShowPrivacyDashboard(false)}
      />

    </Screen>
  );
}

export default function DocumentsScreen(props: DocumentsScreenProps = {}) {
  return (
    <TripErrorBoundary name="DocumentsScreen">
      <DocumentsScreenInner {...props} />
    </TripErrorBoundary>
  );
}
